import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../apiBase';
import type { Locale } from '../studioLocale';
import { StudioIcon } from './StudioIcons';

interface RegistryItem {
  canonical_source: string;
  source: string;
  title?: string;
  status: 'pending' | 'success' | 'blocked' | 'failed' | 'retry';
  quality_score?: number;
  content_hash?: string;
  discovered_at?: string;
  last_attempt_at?: string;
  last_success_at?: string;
}

interface RegistrySummary {
  total: number;
  items: RegistryItem[];
  summary: {
    success: number;
    pending: number;
    blocked: number;
    failed: number;
  };
}

interface XiaohongshuResearchPanelProps {
  locale: Locale;
  onStatusChange?: (status: string) => void;
}

const COPY = {
  zh: {
    title: '小红书研究',
    eyebrow: '资料抓取',
    captureCurrent: '抓取 Chrome 当前页',
    captureCurrentHint: '把当前打开的笔记详情页抓进研究库',
    urlIngest: '链接批量抓取',
    urlPlaceholder: '粘贴小红书链接，多个用换行分隔',
    ingestBtn: '开始抓取',
    registryTitle: '抓取账本',
    pending: '待处理',
    success: '成功',
    blocked: '被拦截',
    failed: '失败',
    processNext: '处理下一个',
    processNextHint: '从账本中取一条 pending 逐条抓取',
    promote: '晋升知识库',
    promoteHint: '把高质量笔记写入永久知识库',
    qualityThreshold: '质量分门槛',
    emptyRegistry: '账本为空',
    capturing: '正在抓取…',
    saved: '已保存',
    error: '出错',
    expand: '展开',
    collapse: '收起',
  },
  en: {
    title: 'Xiaohongshu Research',
    eyebrow: 'Content Capture',
    captureCurrent: 'Capture Current Chrome Tab',
    captureCurrentHint: 'Capture the currently open note detail page into the research library',
    urlIngest: 'URL Batch Capture',
    urlPlaceholder: 'Paste Xiaohongshu links, one per line',
    ingestBtn: 'Start Capture',
    registryTitle: 'Capture Registry',
    pending: 'Pending',
    success: 'Success',
    blocked: 'Blocked',
    failed: 'Failed',
    processNext: 'Process Next',
    processNextHint: 'Pick one pending item from registry and capture it',
    promote: 'Promote to Knowledge Base',
    promoteHint: 'Write high-quality notes into the persistent knowledge base',
    qualityThreshold: 'Quality Threshold',
    emptyRegistry: 'Registry is empty',
    capturing: 'Capturing…',
    saved: 'Saved',
    error: 'Error',
    expand: 'Expand',
    collapse: 'Collapse',
  },
} as const;

const XiaohongshuResearchPanel: React.FC<XiaohongshuResearchPanelProps> = ({
  locale,
  onStatusChange,
}) => {
  const text = COPY[locale];
  const [registry, setRegistry] = useState<RegistrySummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [urls, setUrls] = useState('');
  const [lastResult, setLastResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedStatuses, setExpandedStatuses] = useState<Set<string>>(new Set());
  const [qualityThreshold, setQualityThreshold] = useState(7.8);

  const notify = useCallback(
    (msg: string) => {
      if (onStatusChange) onStatusChange(msg);
    },
    [onStatusChange]
  );

  const fetchRegistry = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/research/xiaohongshu/registry`);
      setRegistry(response.data);
    } catch (e) {
      // silently fail on registry refresh
    }
  }, []);

  useEffect(() => {
    fetchRegistry();
    const interval = setInterval(fetchRegistry, 30000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchRegistry();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchRegistry]);

  const handleCaptureCurrent = async () => {
    setActionLoading('capture');
    setError(null);
    setLastResult(null);
    try {
      const response = await axios.post(`${API_BASE}/api/research/xiaohongshu/capture_current`, {
        max_images: 6,
        wait_ms: 2200,
      });
      setLastResult(response.data);
      notify(`${text.saved}: ${response.data.entry?.title || 'OK'}`);
      fetchRegistry();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e.message || String(e);
      setError(msg);
      notify(`${text.error}: ${msg}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleIngest = async () => {
    const lines = urls
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    if (lines.length === 0) return;
    setActionLoading('ingest');
    setError(null);
    setLastResult(null);
    try {
      const response = await axios.post(`${API_BASE}/api/research/xiaohongshu/ingest`, {
        urls: lines.slice(0, 6),
        max_notes: 6,
        max_images: 6,
        wait_ms: 2800,
      });
      setLastResult(response.data);
      notify(`${text.saved}: ${response.data.saved_count || 0} entries`);
      setUrls('');
      fetchRegistry();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e.message || String(e);
      setError(msg);
      notify(`${text.error}: ${msg}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleProcessNext = async () => {
    setActionLoading('process');
    setError(null);
    setLastResult(null);
    try {
      const response = await axios.post(`${API_BASE}/api/research/xiaohongshu/process_next`, {
        max_images: 4,
        wait_ms: 2800,
      });
      setLastResult(response.data);
      if (response.data.processed) {
        const statusLabel = response.data.status === 'success' ? text.saved : text.error;
        notify(`${statusLabel}: ${response.data.source_url || ''}`);
      } else {
        notify(response.data.reason || 'no_pending_urls');
      }
      fetchRegistry();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e.message || String(e);
      setError(msg);
      notify(`${text.error}: ${msg}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePromote = async () => {
    setActionLoading('promote');
    setError(null);
    try {
      const response = await axios.post(`${API_BASE}/api/research/promote_workspace_entries`, {
        min_quality_score: qualityThreshold,
      });
      notify(`${text.saved}: ${response.data.promoted_count || 0} promoted`);
      setLastResult(response.data);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e.message || String(e);
      setError(msg);
      notify(`${text.error}: ${msg}`);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleStatus = (status: string) => {
    setExpandedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const isLoading = (key: string) => actionLoading === key;

  const filteredItems = (status: string) =>
    (registry?.items || []).filter((item) => item.status === status);

  return (
    <section className="panel-card xhs-research-panel">
      <div className="panel-card__header">
        <div className="panel-title">
          <span className="panel-title__icon">
            <StudioIcon name="search" />
          </span>
          <div>
            <div className="panel-card__eyebrow">{text.eyebrow}</div>
            <h2>{text.title}</h2>
          </div>
        </div>
      </div>

      {/* Capture Current */}
      <div className="xhs-section">
        <button
          className="app-button app-button--strong xhs-btn"
          type="button"
          onClick={handleCaptureCurrent}
          disabled={isLoading('capture')}
        >
          <span className="app-button__content">
            <StudioIcon name="download" />
            <span>{isLoading('capture') ? text.capturing : text.captureCurrent}</span>
          </span>
        </button>
        <p className="xhs-hint">{text.captureCurrentHint}</p>
      </div>

      {/* URL Batch */}
      <div className="xhs-section">
        <label className="form-label">{text.urlIngest}</label>
        <textarea
          className="app-textarea app-textarea--compact"
          rows={3}
          value={urls}
          onChange={(e) => setUrls(e.target.value)}
          placeholder={text.urlPlaceholder}
        />
        <button
          className="app-button xhs-btn"
          type="button"
          onClick={handleIngest}
          disabled={isLoading('ingest') || !urls.trim()}
        >
          <span className="app-button__content">
            <StudioIcon name="download" />
            <span>{isLoading('ingest') ? text.capturing : text.ingestBtn}</span>
          </span>
        </button>
      </div>

      {/* Registry */}
      <div className="xhs-section">
        <div className="xhs-registry-header">
          <strong>{text.registryTitle}</strong>
          {registry && (
            <span className="xhs-badge-group">
              <span className="xhs-badge xhs-badge--pending">{text.pending} {registry.summary.pending}</span>
              <span className="xhs-badge xhs-badge--success">{text.success} {registry.summary.success}</span>
              <span className="xhs-badge xhs-badge--blocked">{text.blocked} {registry.summary.blocked}</span>
              <span className="xhs-badge xhs-badge--failed">{text.failed} {registry.summary.failed}</span>
            </span>
          )}
        </div>

        {!registry && <p className="empty-copy">{text.emptyRegistry}</p>}

        {registry && registry.total === 0 && <p className="empty-copy">{text.emptyRegistry}</p>}

        {registry && registry.total > 0 && (
          <div className="xhs-registry-list">
            {(['pending', 'success', 'blocked', 'failed'] as const).map((status) => {
              const items = filteredItems(status);
              if (items.length === 0) return null;
              const expanded = expandedStatuses.has(status);
              return (
                <details
                  key={status}
                  className={`xhs-registry-group xhs-registry-group--${status}`}
                  open={expanded}
                  onToggle={(e) => {
                    // @ts-ignore
                    toggleStatus(status);
                  }}
                >
                  <summary className="xhs-registry-summary">
                    <span className={`xhs-status-dot xhs-status-dot--${status}`} />
                    <span className="xhs-status-label">
                      {status === 'pending' && text.pending}
                      {status === 'success' && text.success}
                      {status === 'blocked' && text.blocked}
                      {status === 'failed' && text.failed}
                    </span>
                    <span className="xhs-count">{items.length}</span>
                  </summary>
                  <ul className="xhs-item-list">
                    {items.slice(0, 20).map((item, idx) => (
                      <li key={`${item.canonical_source}-${idx}`} className="xhs-item">
                        <span className="xhs-item-title">{item.title || item.source}</span>
                        {item.quality_score ? (
                          <span className="xhs-item-score">{item.quality_score.toFixed(1)}</span>
                        ) : null}
                      </li>
                    ))}
                    {items.length > 20 && (
                      <li className="xhs-item xhs-item--more">+{items.length - 20} more</li>
                    )}
                  </ul>
                </details>
              );
            })}
          </div>
        )}
      </div>

      {/* Process Next */}
      <div className="xhs-section">
        <button
          className="app-button xhs-btn"
          type="button"
          onClick={handleProcessNext}
          disabled={isLoading('process') || (registry?.summary.pending || 0) === 0}
        >
          <span className="app-button__content">
            <StudioIcon name="refresh" />
            <span>{isLoading('process') ? text.capturing : text.processNext}</span>
          </span>
        </button>
        <p className="xhs-hint">{text.processNextHint}</p>
      </div>

      {/* Promote */}
      <div className="xhs-section">
        <label className="form-label">{text.qualityThreshold}</label>
        <input
          className="app-input"
          type="number"
          min={1}
          max={10}
          step={0.1}
          value={qualityThreshold}
          onChange={(e) => setQualityThreshold(Number(e.target.value))}
        />
        <button
          className="app-button app-button--ghost xhs-btn"
          type="button"
          onClick={handlePromote}
          disabled={isLoading('promote')}
        >
          <span className="app-button__content">
            <StudioIcon name="library" />
            <span>{isLoading('promote') ? text.capturing : text.promote}</span>
          </span>
        </button>
        <p className="xhs-hint">{text.promoteHint}</p>
      </div>

      {/* Result / Error */}
      {error && <div className="form-error xhs-error">{error}</div>}
      {lastResult && !error && (
        <div className="xhs-result">
          {lastResult.entry?.title && (
            <div className="xhs-result-title">{lastResult.entry.title}</div>
          )}
          {lastResult.entry?.quality_score !== undefined && (
            <div className="xhs-result-score">Quality: {lastResult.entry.quality_score.toFixed(1)}</div>
          )}
          {lastResult.saved_count !== undefined && (
            <div className="xhs-result-count">Saved: {lastResult.saved_count}</div>
          )}
          {lastResult.promoted_count !== undefined && (
            <div className="xhs-result-count">Promoted: {lastResult.promoted_count}</div>
          )}
        </div>
      )}
    </section>
  );
};

export default XiaohongshuResearchPanel;
